// Sidebar shell layout — wraps all authenticated pages.
// DESIGN-SYSTEM.md §Layout Shell.

import { useState, useCallback, useRef } from "react";
import type { JSX, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth.js";
import { useWebSocket, type WsEvent } from "../hooks/useWebSocket.js";
import { apiFetch } from "../lib/api.js";
import { NotificationsDrawer } from "./NotificationsDrawer.js";
import type { Notification } from "./NotificationsDrawer.js";
import { CallScreenPop, type InboundCallPayload } from "./CallScreenPop.js";
import { Sidebar, type TenantConfigResponse } from "./Sidebar.js";
import { BottomNav } from "./BottomNav.js";
import { MobileNavDrawer } from "./MobileNavDrawer.js";
import { RiskCriticalBanner } from "./RiskCriticalBanner.js";
import { severityForEventType, severityForNotification } from "../lib/notification-severity.js";

interface NotificationsResponse { data: Notification[]; total: number; page: number; limit: number; }

// Q3 — Critical-alert chime. Procedurally-generated double-beep at 880Hz
// (~0.45s total) shipped at apps/web/public/sounds/critical-alert.wav per
// TASK-36. Asset is intentionally synthesized rather than licensed/sourced
// to avoid third-party licensing; product/design can swap it for a designed
// chime later by replacing the file at the same path. play() rejection is
// still caught and logged in case the browser autoplay policy intervenes.
const CRITICAL_CHIME_SRC = "/sounds/critical-alert.wav";

function BellIcon(): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function playCriticalChime(audioRef: React.RefObject<HTMLAudioElement | null>): void {
  const el = audioRef.current;
  if (!el) return;
  el.currentTime = 0;
  const result = el.play();
  if (result instanceof Promise) {
    result.catch((err: unknown) => {
      // Browser autoplay policy may reject until user interaction. Banner is the
      // primary signal; chime is enhancement only.
      console.warn("[Q3] critical chime play() rejected:", err);
    });
  }
}

interface TopHeaderProps {
  tenantName: string;
  unreadCount: number;
  hasCritical: boolean;
  userEmail: string | undefined;
  onOpenNotifications: () => void;
  onOpenMobileNav: () => void;
  onLogout: () => void;
}

function TopHeader(props: TopHeaderProps): JSX.Element {
  const { tenantName, unreadCount, hasCritical, userEmail, onOpenNotifications, onOpenMobileNav, onLogout } = props;
  const badgeBase = hasCritical
    ? "bg-red-600 text-white text-[11px] font-bold rounded-full w-5 h-5 animate-pulse ring-2 ring-red-300"
    : "bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4";
  return (
    <header className="h-12 bg-white border-b border-slate-200 flex items-center justify-between px-4 shrink-0 sticky top-0 z-20">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onOpenMobileNav}
          className="md:hidden p-1 -ml-1 text-slate-600 hover:text-slate-900"
          aria-label="Open navigation"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <span
          className="text-base font-medium text-navy-900 truncate max-w-[16rem]"
          title={tenantName}
        >
          {tenantName || "—"}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onOpenNotifications}
          className="relative p-2 rounded-full hover:bg-slate-100 transition-colors text-slate-600"
          aria-label="Open notifications"
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span className={`absolute -top-1 -right-1 ${badgeBase} flex items-center justify-center`}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
        <span className="text-xs text-slate-500 truncate max-w-[12rem]" title={userEmail}>
          {userEmail}
        </span>
        <button
          onClick={onLogout}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

export function Shell({ children }: { children: ReactNode }): JSX.Element {
  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [callEvent,   setCallEvent]   = useState<InboundCallPayload | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const qc = useQueryClient();
  const { user, logout } = useAuth();

  const { data: tenantData } = useQuery<TenantConfigResponse>({
    queryKey: ["tenant-config"],
    queryFn: () => apiFetch<TenantConfigResponse>("/tenants/me/config"),
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  const handleWsEvent = useCallback((ev: WsEvent): void => {
    const { event_type, payload } = ev;
    if (event_type === "call.inbound") {
      setCallEvent(payload as unknown as InboundCallPayload);
    }
    // Q3 — layered risk-alert dispatch by event severity
    const severity = severityForEventType(event_type);
    if (severity === "critical") {
      playCriticalChime(audioRef);
      void qc.invalidateQueries({ queryKey: ["sr-queue"] });
    } else if (severity === "red") {
      const title = (payload["title"] as string | undefined) ?? "Risk: Red";
      const body = (payload["body"] as string | undefined) ?? "";
      toast(title, { description: body, duration: 60_000, className: "bg-orange-500 text-white" });
      void qc.invalidateQueries({ queryKey: ["sr-queue"] });
    }
    if (event_type === "sr.assigned" || event_type === "sr.new" ||
        event_type === "visit_report.submitted") {
      void qc.invalidateQueries({ queryKey: ["sr-queue"] });
    }
    if (event_type === "patient.consent_changed") {
      void qc.invalidateQueries({ queryKey: ["patient"] });
    }
    void qc.invalidateQueries({ queryKey: ["notifications"] });
  }, [qc]);

  useWebSocket(handleWsEvent);

  const { data } = useQuery<NotificationsResponse>({
    queryKey: ["notifications"],
    queryFn: () => apiFetch<NotificationsResponse>("/notifications?limit=20&unread_only=false"),
    refetchInterval: 30_000,
  });

  const items = data?.data ?? [];
  const unreadCount = items.filter((n) => n.read_at === null).length;
  // TASK-35: prefer the persisted severity column; fall back to event_type
  // mapping for legacy rows written before TASK-35.
  const criticalUnread = items.find(
    (n) => n.read_at === null && severityForNotification(n) === "critical",
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        {criticalUnread && <RiskCriticalBanner notification={criticalUnread} />}
        <TopHeader
          tenantName={tenantData?.name ?? ""}
          unreadCount={unreadCount}
          hasCritical={Boolean(criticalUnread)}
          userEmail={user?.email}
          onOpenNotifications={() => setDrawerOpen(true)}
          onOpenMobileNav={() => setMobileNavOpen(true)}
          onLogout={() => { void logout(); }}
        />
        <main className="flex-1 overflow-y-auto pb-16 md:pb-0">{children}</main>
      </div>
      <BottomNav onOpenMore={() => setMobileNavOpen(true)} />
      <MobileNavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <NotificationsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={items}
      />
      <CallScreenPop event={callEvent} onDismiss={() => setCallEvent(null)} />
      <audio ref={audioRef} src={CRITICAL_CHIME_SRC} preload="auto" />
    </div>
  );
}
